import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { StageSignOff, SubmitSignOffRequest } from '@/types/onboarding';
import { useToast } from '@/hooks/use-toast';

// ============================================================================
// Types
// ============================================================================
export interface SignOffActivity {
  id: string;
  project_id: string;
  stage_name: string;
  action: 'created' | 'submitted' | 'approved' | 'rejected' | 'invitation_sent' | 'invitation_viewed';
  user_id?: string;
  user_name?: string;
  user_email?: string;
  timestamp: string;
  details?: Record<string, any>;
}

export interface CustomerInvitation {
  id: string;
  project_id: string;
  customer_email: string;
  customer_name?: string;
  personal_message?: string;
  status: 'pending' | 'sent' | 'viewed' | 'completed' | 'expired';
  sent_at?: string;
  expires_at: string;
  completed_at?: string;
}

export interface InviteCustomerRequest {
  customer_email: string;
  customer_name?: string;
  personal_message?: string;
  expires_days?: number;
}

// ============================================================================
// useSignOffs - Load all sign-offs for a project
// ============================================================================
export function useSignOffs(projectId: string | undefined) {
  return useQuery({
    queryKey: ['signoffs', projectId],
    queryFn: async (): Promise<StageSignOff[]> => {
      if (!projectId) return [];

      const { data, error } = await (supabase as any)
        .from('tum_onboarding_stage_signoffs')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []) as StageSignOff[];
    },
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

// ============================================================================
// useSubmitSignOff - Submit a sign-off approval/rejection
// ============================================================================
export function useSubmitSignOff() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      projectId, 
      request 
    }: { 
      projectId: string; 
      request: SubmitSignOffRequest;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Find existing sign-off for this stage
      const { data: existing, error: findError } = await (supabase as any)
        .from('tum_onboarding_stage_signoffs')
        .select('id')
        .eq('project_id', projectId)
        .eq('stage_name', request.stage_name)
        .single();

      if (findError && findError.code !== 'PGRST116') throw findError;

      const signOffData = {
        project_id: projectId,
        stage_name: request.stage_name,
        status: request.status,
        signed_by_user_id: user.id,
        signed_at: new Date().toISOString(),
        comments: request.comments || null,
        conditions_met: request.conditions_met || {},
        updated_at: new Date().toISOString(),
      };

      if (existing) {
        const { data, error } = await (supabase as any)
          .from('tum_onboarding_stage_signoffs')
          .update(signOffData)
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return data as StageSignOff;
      } else {
        const { data, error } = await (supabase as any)
          .from('tum_onboarding_stage_signoffs')
          .insert({
            ...signOffData,
            required_signoff: true,
            signoff_role: request.stage_name === 'customer_signoff' ? 'customer' : 'internal',
          })
          .select()
          .single();

        if (error) throw error;
        return data as StageSignOff;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['signoffs', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit sign-off',
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// useInviteCustomer - Send customer invitation for sign-off
// ============================================================================
export function useInviteCustomer() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      projectId,
      request,
    }: {
      projectId: string;
      request: InviteCustomerRequest;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get project details for email
      const { data: project, error: projectError } = await (supabase as any)
        .from('tum_onboarding_projects')
        .select('project_name, customer_name, workspace_id')
        .eq('id', projectId)
        .single();

      if (projectError) throw projectError;

      // Call edge function to send notification
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('send-signoff-notification', {
        body: {
          action: 'invite_customer',
          project_id: projectId,
          project_name: project.project_name,
          customer_name: request.customer_name || project.customer_name,
          customer_email: request.customer_email,
          personal_message: request.personal_message,
          expires_days: request.expires_days || 7,
          invited_by_user_id: user.id,
          workspace_id: project.workspace_id,
        },
      });

      if (response.error) throw new Error(response.error.message);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['signoffs', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['signoff-history', variables.projectId] });
      toast({
        title: 'Invitation Sent',
        description: 'Customer has been invited to sign off on this project',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to Send Invitation',
        description: error.message || 'Could not send customer invitation',
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// useSignOffHistory - Load sign-off activity history
// ============================================================================
export function useSignOffHistory(projectId: string | undefined) {
  return useQuery({
    queryKey: ['signoff-history', projectId],
    queryFn: async (): Promise<SignOffActivity[]> => {
      if (!projectId) return [];

      // Get sign-offs to build history
      const { data: signoffs, error } = await (supabase as any)
        .from('tum_onboarding_stage_signoffs')
        .select('*')
        .eq('project_id', projectId)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // Build activity history from sign-offs
      const activities: SignOffActivity[] = [];

      for (const signoff of signoffs || []) {
        // Add creation event
        activities.push({
          id: `${signoff.id}-created`,
          project_id: projectId,
          stage_name: signoff.stage_name,
          action: 'created',
          timestamp: signoff.created_at,
        });

        // Add sign-off event if signed
        if (signoff.signed_at && signoff.status !== 'pending') {
          activities.push({
            id: `${signoff.id}-${signoff.status}`,
            project_id: projectId,
            stage_name: signoff.stage_name,
            action: signoff.status === 'approved' ? 'approved' : 'rejected',
            user_id: signoff.signed_by_user_id,
            timestamp: signoff.signed_at,
            details: {
              comments: signoff.comments,
              conditions_met: signoff.conditions_met,
            },
          });
        }
      }

      // Sort by timestamp descending
      return activities.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    },
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

// ============================================================================
// useCustomerInvitations - Load customer invitations for a project
// ============================================================================
export function useCustomerInvitations(projectId: string | undefined) {
  return useQuery({
    queryKey: ['customer-invitations', projectId],
    queryFn: async (): Promise<CustomerInvitation[]> => {
      if (!projectId) return [];

      const { data, error } = await (supabase as any)
        .from('tum_onboarding_customer_invitations')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error && error.code !== 'PGRST116') throw error;

      return (data || []).map((inv: any) => ({
        id: inv.id,
        project_id: inv.project_id,
        customer_email: inv.invited_email,
        customer_name: inv.invited_name,
        personal_message: inv.personal_message,
        status: inv.status || 'pending',
        sent_at: inv.sent_at || inv.created_at,
        expires_at: inv.expires_at,
        completed_at: inv.completed_at,
      }));
    },
    enabled: !!projectId,
    staleTime: 30_000,
  });
}
